import {
  hasActiveVariants,
  isLegacy,
  isStarted,
  openExperimentOf
} from "./links";
import { assignmentOf, hasWeights, quotaOf, strategyOf } from "./model";
import { isPendingPage } from "./pages";

const STARTED = "The Experiment has started: Variants and Traffic are locked";

// What decides who sees what, and so can't change once the experiment has
// started: the endpoint, who assigns and how, the start, and the variants
// (id, value, page, control, order, weights)
const locked = (data = {}) =>
  JSON.stringify({
    referenceId: data.referenceId ?? null,
    assignment: assignmentOf(data),
    strategy: assignmentOf(data) === "ares" ? strategyOf(data) : null,
    start: data.scheduling?.start ?? null
  });

const lockedVariations = (data = {}) =>
  JSON.stringify(
    (data.variations ?? []).map((v, index) => ({
      id: v.id ?? null,
      value: String(v.value ?? "").trim(),
      blockId: v.blockId ?? null,
      control: index === 0,
      traffic: hasWeights(data) ? Number(v.traffic) || 0 : null
    }))
  );

// The errors of the experiment (none: {}). The messages are the ones of the
// form; `settings` and `variations` light the warning of their box.
// - origin: the stored row, none for a new experiment;
// - endpoint: the row of the endpoint of the experiment;
// - experiments: the rows of the experiments (an endpoint has one open);
// - blockExists(id): the page exists (a variant marked created that has no
//   page yet gets it with the Save).
export default function validate(
  data = {},
  { origin, endpoint, experiments, blockExists = () => true } = {}
) {
  const errors = {};

  // Promoted or closed: nothing is left to check, the experiment is history
  if (data.outcome) return errors;

  const variations = data.variations ?? [];
  const started = isStarted(origin);
  const starting = Boolean(data.running) && !started;

  if (!data.referenceId) {
    errors.reference = "You Must Select an Endpoint";
    errors.settings = true;
  } else {
    const other = openExperimentOf(data.referenceId, experiments, {
      legacy: true
    });

    if (other && other.id !== origin?.id) {
      errors.reference = isLegacy(other)
        ? `The Endpoint has an experiment of the old format: delete ${
            other.title || other.id
          } first`
        : `The Endpoint already has an open experiment: ${
            other.title || other.id
          }`;
      errors.settings = true;
    } else if (starting && !endpoint) {
      errors.reference = "The Endpoint doesn't exist";
      errors.settings = true;
    } else if (starting && hasActiveVariants(endpoint)) {
      errors.reference = "The Endpoint has active variants: disable them first";
      errors.settings = true;
    } else if (starting && !endpoint.enableAnalitica) {
      errors.reference =
        "Enable Analitica on the Endpoint to start the experiment";
      errors.settings = true;
    }
  }

  if (!data.scheduling?.start || !data.scheduling?.end) {
    errors.scheduling = "You Must Pick a Date Range";
    errors.settings = true;
  } else if (
    new Date(data.scheduling.start).getTime() >=
    new Date(data.scheduling.end).getTime()
  ) {
    errors.scheduling = "The End Must Be After the Start";
    errors.settings = true;
  }

  if (assignmentOf(data) === "ares") {
    const quota = quotaOf(data);

    if (quota === "" || !(Number(quota) >= 1 && Number(quota) <= 100)) {
      errors.quota = "The Quota Must Be Between 1 and 100";
      errors.settings = true;
    } else if (started && Number(quota) < Number(quotaOf(origin))) {
      errors.quota = "After the start the Quota can only rise";
      errors.settings = true;
    }
  }

  if (variations.length < 2) {
    errors.variations = "You Must Add at least two Variants";
  }

  const counts = new Map();
  variations.forEach((v) => {
    const value = String(v.value ?? "").trim();
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  const pages = new Set();
  const variants = new Set((endpoint?.variants ?? []).map((v) => v.blockId));
  const fail = (v, field, message) => {
    errors.variations = errors.variations || true;
    errors[v.id] = message;
    errors[`${v.id}:${field}`] = message;
  };

  variations.forEach((v, index) => {
    const value = String(v.value ?? "").trim();

    if (!v.label) fail(v, "label", "You Must enter a Name");
    if (!value) fail(v, "value", "You Must enter a Value");
    else if (counts.get(value) > 1)
      fail(v, "value", "The Value Must Be Unique");

    if (!v.blockId) fail(v, "page", "You Must Select a Page");
    else if (pages.has(v.blockId))
      fail(v, "page", "The Page is already used by another Variant");
    else if (endpoint && index === 0 && v.blockId !== endpoint.blockId)
      fail(v, "page", "The Control Must Be the Default Page of the Endpoint");
    else if (
      endpoint &&
      index > 0 &&
      !variants.has(v.blockId) &&
      !isPendingPage(v, (id) => blockExists(id))
    )
      fail(v, "page", "The Page Must Be a Variant of the Endpoint");
    pages.add(v.blockId);
  });

  if (hasWeights(data)) {
    const traffic = variations.reduce(
      (sum, v) => sum + (Number(v.traffic) || 0),
      0
    );

    if (traffic !== 100) {
      errors.traffic = true;
      errors.variations = "Traffic Must Sum to 100%";
    }
  }

  if (started) {
    // The label of the experiment in the Analitica records
    if ((data.title ?? "") !== (origin.title ?? ""))
      errors.title = "The Experiment has started: the Title is locked";
    if (locked(data) !== locked(origin)) {
      errors.settings = true;
      errors.reference = errors.reference || STARTED;
    }
    if (lockedVariations(data) !== lockedVariations(origin))
      errors.variations = STARTED;
  }

  return errors;
}
